import { useEffect, useRef, useState, useSyncExternalStore } from 'react'
import { subscribe, getCount } from './activityCounter'

// Owner-Vorgabe (AUFGABE 2): "waehrend etwas im Hintergrund arbeitet hat der
// Nutzer keinerlei Hinweis, dass etwas laeuft". Anti-Flacker-Fenster: ein
// Balken, der bei jedem 40-ms-Aufruf aufblitzt, ist schlimmer als keiner —
// erst nach SHOW_DELAY_MS echter Laufzeit einblenden, dann mindestens
// MIN_VISIBLE_MS stehen lassen, bevor er wieder verschwindet.
const SHOW_DELAY_MS = 250
const MIN_VISIBLE_MS = 400

/**
 * Anwendungsweite Anzeige fuer laufende /api/*-Anfragen.
 *
 * Zaehlpunkt ist activityCounter.js, gespeist von
 * sessionFetchInterceptor.ts — dem einen Ort, durch den JEDER /api/*-
 * Aufruf laeuft (react-query UND handgeschriebene fetch()s gleichermassen;
 * useIsFetching() allein wuerde Letztere uebersehen).
 *
 * Schmaler Balken ganz oben am Fenster, fixiert, ueber allem, ohne Klicks
 * zu schlucken (pointer-events: none). Unbestimmter Fortschritt (Dauer ist
 * unbekannt) ueber dieselbe indeterminate-progress-Keyframe wie der
 * Upload-Balken im ImportWizard. Tailwinds motion-safe:-Praefix schaltet
 * die Animation bei prefers-reduced-motion automatisch ab — es bleibt ein
 * ruhiger, statischer Balken stehen (Bewegung bleibt bei 4/10, DESIGN.md).
 */
export default function Aktivitaetsanzeige() {
  const count = useSyncExternalStore(subscribe, getCount)
  const active = count > 0
  const [visible, setVisible] = useState(false)
  const shownAtRef = useRef<number | null>(null)

  useEffect(() => {
    if (active) {
      // Schon sichtbar -> nichts zu tun (z.B. eine zweite Anfrage startet,
      // waehrend die erste noch laeuft).
      if (shownAtRef.current !== null) return undefined
      const showTimer = setTimeout(() => {
        shownAtRef.current = Date.now()
        setVisible(true)
      }, SHOW_DELAY_MS)
      return () => clearTimeout(showTimer)
    }

    // Nie eingeblendet (alle Anfragen waren kuerzer als SHOW_DELAY_MS) ->
    // kein Ausblenden noetig, kein Flackern.
    if (shownAtRef.current === null) return undefined
    const remaining = Math.max(0, MIN_VISIBLE_MS - (Date.now() - shownAtRef.current))
    const hideTimer = setTimeout(() => {
      shownAtRef.current = null
      setVisible(false)
    }, remaining)
    return () => clearTimeout(hideTimer)
  }, [active])

  if (!visible) return null

  return (
    <div
      role="progressbar"
      aria-busy="true"
      aria-label="Daten werden geladen"
      className="fixed top-0 inset-x-0 z-[9999] h-[3px] overflow-hidden bg-primary/10 pointer-events-none"
    >
      <span className="sr-only">Daten werden geladen</span>
      <div className="absolute inset-y-0 left-0 w-[35%] bg-primary motion-safe:animate-[indeterminate-progress_1.4s_ease-in-out_infinite]" />
    </div>
  )
}
